import { isFunction } from 'lodash';
import * as actionTypes from './mainUIActionTypes';
import { validateSessionConnection } from './session';
import React from 'react';

export const toggleNavPanel = (show) => ({
  type: actionTypes.MAINUI_NAV_PANEL_TOGGLE,
  show,
});

export const triggerNavPanelComponent = (component) => {
  return {
    type: actionTypes.TRIGGER_NAV_PANEL_COMPONENT,
    payload: component,
  };
};

export const toggleWorkspacePreview = (show) => ({
  type: actionTypes.MAINUI_WORKSPACE_PREVIEW_TOGGLE,
  show,
});

export const toggleAdvancedSearch = (show) => ({
  type: actionTypes.MAINUI_ADVANCED_SEARCH_TOGGLE,
  show,
});

export const toggleUMODetailView = (show) => ({
  type: actionTypes.MAINUI_UMO_DETAIL_VIEW_TOGGLE,
  show,
});
export const setMetaUmoId = (id) => ({
  type: actionTypes.MAINUI_SET_META_PANEL_ID,
  id,
});

export const toggleMetaPanelView = (show) => ({
  type: actionTypes.MAINUI_META_PANEL_TOGGLE,
  show,
});

export const toggleCompareView = (show) => ({
  type: actionTypes.MAINUI_UMO_COMPARE_VIEW_TOGGLE,
  show,
});

export const toggleWorkspaceDetailView = (show, modalWorkspaceID) => ({
  type: actionTypes.MAINUI_WORKSPACE_DETAIL_VIEW_TOGGLE,
  show,
  modalWorkspaceID,
});

export const showUMOGridView = () => ({
  type: actionTypes.MAINUI_UMO_GRID_VIEW,
});

export const showUMOGridNoJView = () => ({
  type: actionTypes.MAINUI_UMO_GRID_NOJ_VIEW,
});

export const showUMOListView = () => ({
  type: actionTypes.MAINUI_UMO_LIST_VIEW,
});

export const showUMOSmGridNoJView = () => ({
  type: actionTypes.MAINUI_UMO_SM_GRID_NOJ_VIEW,
});

export const showUMOCarouselView = () => ({
  type: actionTypes.MAINUI_UMO_CAROUSEL_VIEW,
});

export const toggleUMOSortView = (show) => ({
  type: actionTypes.MAINUI_MANUAL_SORT_TOGGLE,
  show,
});

export const showSearchAsMain = () => ({
  type: actionTypes.MAINUI_SHOW_MAIN_SEARCH,
});

export const showHighlightsAsMain = () => ({
  type: actionTypes.MAINUI_SHOW_HIGHLIGHTS,
});

/**
 * Success action for showing internal link (/external-tools)
 * @param url is the url string
 * @param urlFunction is the given function that was used to build/retrieve the url. it is used to
 * confirm that the success action was for the correct "loading" action.
 * @returns {{type, url: *}}
 */
export const showAdminAsMainSuccess = (url, urlFunction) => {
  const data = {
    type: actionTypes.MAINUI_SHOW_MAIN_ADMIN_SUCCESS,
    url,
  };

  if (urlFunction) {
    data.urlFunction = urlFunction;
  }

  return data;
};

/**
 * Failure action for showing internal link (/external-tools)
 * @param err is the error
 * @param urlFunction see success action for more info on this param
 * @returns {{type, url: *}}
 */
export const showAdminAsMainFailure = (err, urlFunction) => {
  const data = {
    type: actionTypes.MAINUI_SHOW_MAIN_ADMIN_FAILURE,
    err,
  };

  if (urlFunction) {
    data.urlFunction = urlFunction;
  }

  return data;
};

export const showAdminAsMainLoading = (urlFunction) => ({
  type: actionTypes.MAINUI_SHOW_MAIN_ADMIN_LOADING,
  urlFunction,
});

/**
 * Shows internal url as the main content. Main purpose is to show admin screen
 * identical to ShowExternalAsMain- except the source is in the iframe vs from a <form> POST
 * @param url can be a string or a function that returns a promise resolved with a string
 * @param surl - session url, for connection validation
 */
export const showAdminAsMain = (url, surl) =>
  function showAdminAsMainDispatch(dispatch) {
    dispatch(validateSessionConnection(surl)).then((res) => {
      if (res.isValid) {
        if (isFunction(url)) {
          dispatch(showAdminAsMainLoading(url));
          return url()
            .then((urlString) => {
              dispatch(showAdminAsMainSuccess(urlString, url));
            })
            .catch((error) => {
              dispatch(showAdminAsMainFailure(error, url));
            });
        }
        dispatch(showAdminAsMainSuccess(url));
      }
      return Promise.resolve();
    });
  };

export const showLocalAdminAsMain = (adminType) => ({
  type: actionTypes.MAINUI_SHOW_ADMIN_SCREEN,
  adminType,
});

export const showEditHistoryAsMain = (recordType) => ({
  type: actionTypes.MAINUI_SHOW_RECORD_SCREEN,
  recordType,
});

export const showBulkMetaEditAsMain = () => ({
  type: actionTypes.MAINUI_BULK_META_EDIT,
});

/**
 * Success action for showing external link
 * @param url is the url string
 * @param urlFunction is the given function that was used to build/retrieve the url. it is used to
 * confirm that the success action was for the correct "loading" action.
 * @returns {{type, url: *}}
 */
export const showExternalAsMainSuccess = (url, urlFunction) => {
  const data = {
    type: actionTypes.MAINUI_SHOW_MAIN_EXTERNAL_SUCCESS,
    url,
  };

  if (urlFunction) {
    data.urlFunction = urlFunction;
  }

  return data;
};

/**
 * Failure action for showing external link
 * @param err is the error
 * @param urlFunction see success action for more info on this param
 * @returns {{type, url: *}}
 */
export const showExternalAsMainFailure = (err, urlFunction) => {
  const data = {
    type: actionTypes.MAINUI_SHOW_MAIN_EXTERNAL_FAILURE,
    err,
  };

  if (urlFunction) {
    data.urlFunction = urlFunction;
  }

  return data;
};

export const showExternalAsMainLoading = (urlFunction) => ({
  type: actionTypes.MAINUI_SHOW_MAIN_EXTERNAL_LOADING,
  urlFunction,
});

/**
 * Shows external url as the main content. Main purpose is to show templates.
 * @param url can be a string or a function that returns a promise resolved with a string
 * @param surl - session url, for connection validation
 */
export const showExternalAsMain = (url, surl) =>
  function showExternalAsMainDispatch(dispatch) {
    dispatch(validateSessionConnection(surl)).then((res) => {
      if (res.isValid) {
        if (isFunction(url)) {
          dispatch(showExternalAsMainLoading(url));
          return url()
            .then((urlString) => {
              dispatch(showExternalAsMainSuccess(urlString, url));
            })
            .catch((error) => {
              dispatch(showExternalAsMainFailure(error, url));
            });
        }
        dispatch(showExternalAsMainSuccess(url));
        const $extForm = document.getElementById('external-content__form');
        // re-set the params- to prevent opening in new tab
        $extForm.target = 'external-content-iframe';
        $extForm.action = url;
        $extForm.method = 'POST';
        // posts a form with hidden input params containing surl and csrf token
        $extForm.submit();
      }
      return Promise.resolve();
    });
  };

export const showMainDialog = (dialogType, dialogData) => ({
  type: actionTypes.MAINUI_SHOW_MAIN_DIALOG,
  dialogType,
  dialogData,
});

export const hideMainDialog = () => ({
  type: actionTypes.MAINUI_HIDE_MAIN_DIALOG,
});

export const showWorkspaceEmailDialog = (workspaceData, isPortal) => ({
  type: actionTypes.MAINUI_SHOW_WORKSPACE_EMAIL_DIALOG,
  workspaceData,
  isPortal,
});

export const hideWorkspaceEmailDialog = () => ({
  type: actionTypes.MAINUI_HIDE_WORKSPACE_EMAIL_DIALOG,
});

export const showUMOModifyDialog = (
  data,
  umoList,
  searchParams,
  modalWorkspaceID
) => ({
  type: actionTypes.MAINUI_SHOW_UMO_MODIFY_DIALOG,
  data,
  umoList,
  searchParams,
  modalWorkspaceID,
});

export const hideUMOModifyDialog = () => ({
  type: actionTypes.MAINUI_HIDE_UMO_MODIFY_DIALOG,
});

export const showCategoryMoveDialog = (categoryID, categoryName) => ({
  type: actionTypes.MAINUI_SHOW_CATEGORY_MOVE_DIALOG,
  categoryID,
  categoryName,
});

export const hideCategoryMoveDialog = () => ({
  type: actionTypes.MAINUI_HIDE_CATEGORY_MOVE_DIALOG,
});

export const toggleFullImageView = (show, umoData) => ({
  type: actionTypes.MAINUI_TOGGLE_FULL_IMAGE_VIEW,
  show,
  umoData,
});

export const showCategoryFileUploader = (
  categoryID,
  categoryName,
  isFileUploaderMounted
) => {
  let categoryToUpload = {
    categoryID,
    categoryName,
    isFileUploaderMounted,
  };
  return {
    type: actionTypes.MAINUI_SHOW_CATEGORY_FILE_UPLOADER,
    categoryToUpload,
  };
};

export const hideCategoryFileUploader = () => {
  return {
    type: actionTypes.MAINUI_HIDE_CATEGORY_FILE_UPLOADER,
  };
};
export const minimizeFileUploader = () => {
  return {
    type: actionTypes.MAINUI_MINIMIZE_CATEGORY_FILE_UPLOADER,
  };
};

export const maximizeFileUploader = () => {
  return {
    type: actionTypes.MAINUI_MAXIMIZE_CATEGORY_FILE_UPLOADER,
  };
};

export const collapseCategoryFileUploader = () => ({
  type: actionTypes.MAINUI_COLLAPSE_CATEGORY_FILE_UPLOADER,
});

export const expandCategoryFileUploader = () => ({
  type: actionTypes.MAINUI_EXPAND_CATEGORY_FILE_UPLOADER,
});

export const isFileUploaderUploading = (isUploading) => ({
  type: actionTypes.MAINUI_FILE_UPLOADER_TOGGLE_UPLOADING,
  isUploading,
});

export const updateCollectionIdBeingEdited = (collectionId) => ({
  type: actionTypes.MAINUI_UPDATE_COLLECTION_ID_BEING_EDITED,
  collectionId,
});
